"use client";
import swrConfig from "@/config/swr";
import { SessionProvider } from "next-auth/react";
import { Suspense, useEffect, useMemo, memo, useState } from "react";
import { SWRConfig } from "swr";
import ReactGA from "react-ga";
import { Toaster } from "react-hot-toast";
import { GlobalProvider } from "./global";
import { NotificationsProvider } from "./notifications";

import { QueryClient, QueryClientProvider } from "react-query";
import Loading from "@/app/loading";
import { TooltipProvider } from "@/components/ui/tooltip";
import GlobalContextMenu from "@/components/ui/global-context-menu";

// Create the client outside component to prevent recreation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Reduce unnecessary refetches
      staleTime: 60000, // 1 minute stale time to reduce refetches
    },
  },
});

// Memoize child components to prevent unnecessary re-renders
const MemoizedNotificationsProvider = memo(NotificationsProvider);
const MemoizedSWRConfig = memo(SWRConfig);
const MemoizedToaster = memo(Toaster);

// Declare types for globalThis extension
declare global {
  var env: Record<string, string>;
}

// Separate environment initialization component to reduce main component complexity
const EnvironmentInitializer = memo(({ env }: { env: any }) => {
  useEffect(() => {
    // Initialize environment variables - only runs once
    globalThis.env = globalThis.env || {};
    for (const v in env) {
      process.env[v] = env[v];
      globalThis.env[v] = env[v];
    }

    if (
      process.env.NODE_ENV === "production" &&
      process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID
    ) {
      ReactGA.initialize(process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID);
    }
  }, [env]);

  return null;
});

EnvironmentInitializer.displayName = "EnvironmentInitializer";

export default function Context(props: {
  siteSettings?: any;
  appVersion: any;
  children?: any;
  env: any;
  enabledFeatures: {
    name: string;
    active: boolean;
    type: string | null;
    otherData: any
  }[]
}) {
  // Memoize SWR options to prevent recreation on each render
  const swrOptions = useMemo(() => swrConfig(), []);

  return (
    <TooltipProvider>
      <EnvironmentInitializer env={props.env} />
      <Suspense fallback={<Loading />}>
        <QueryClientProvider client={queryClient}>
          <GlobalProvider
            siteSettings={props.siteSettings}
            appVersion={props.appVersion}
            enabledFeatures={props.enabledFeatures}
          >
            <SessionProvider>
              <MemoizedSWRConfig value={swrOptions}>
                <MemoizedToaster
                  position="bottom-center"
                  toastOptions={{ duration: 3000 }}
                />
                <MemoizedNotificationsProvider>
                  {props.children}
                  {/* Global context-menu overlay */}
                  <GlobalContextMenu />
                </MemoizedNotificationsProvider>
              </MemoizedSWRConfig>
            </SessionProvider>
          </GlobalProvider>
        </QueryClientProvider>
      </Suspense>
    </TooltipProvider>
  );
}
